
import React from "react";
import { useNavigate } from "react-router-dom";
import { Clock, CheckCircle, XCircle, Star } from "lucide-react";
import { MetricCard } from "@/components/admin/dashboard/MetricCard";

interface BookingMetricsCardsProps {
  serviceId?: string;
  bookingCounts: {
    new: number;
    accepted: number;
    cancelled: number;
    declined: number;
    completed: number;
  };
  isServiceDashboard?: boolean;
  timeFilter?: string;
}

export const BookingMetricsCards = ({ 
  serviceId, 
  bookingCounts, 
  isServiceDashboard = false,
  timeFilter = "upcoming"
}: BookingMetricsCardsProps) => {
  const navigate = useNavigate();
  
  const handleMetricCardClick = (status: string | null) => {
    if (isServiceDashboard && serviceId) {
      // For service dashboard, stay on the same service but change view and filter
      if (window.location.pathname.includes('/bookings')) {
        // Already on bookings page, just apply the filter
        console.log("Already on bookings page, applying filter:", status);
        if (status) {
          sessionStorage.setItem('booking-status-filter', status);
          // Force a refresh by navigating to the same page
          navigate(`/service/${serviceId}/bookings?status=${status}`, { replace: true });
        }
      } else {
        // Not on bookings page, navigate and set filter
        console.log("Navigating to bookings page with filter:", status);
        if (status) {
          sessionStorage.setItem('booking-status-filter', status);
        }
        // Navigate to the bookings page for this service
        navigate(`/service/${serviceId}/bookings`);
      }
    } else {
      // For admin dashboard, navigate to admin bookings with query param
      navigate(`/admin/bookings${status ? `?status=${status}` : ''}`);
    }
  };

  return (
    <div className={`grid grid-cols-2 gap-2 md:gap-4 mb-4 w-full max-w-full overflow-x-hidden ${timeFilter === "upcoming" ? "md:grid-cols-3" : "md:grid-cols-4"}`}>
      <div 
        onClick={() => handleMetricCardClick('new')}
        className="cursor-pointer hover:opacity-90 transition-opacity"
      >
        <MetricCard
          title="New"
          value={bookingCounts.new}
          icon={<Clock className="h-5 w-5 text-yellow-500" />}
          color="bg-yellow-100"
        />
      </div>
      <div 
        onClick={() => handleMetricCardClick('accepted')}
        className="cursor-pointer hover:opacity-90 transition-opacity"
      >
        <MetricCard
          title="Accepted"
          value={bookingCounts.accepted}
          icon={<CheckCircle className="h-5 w-5 text-green-500" />}
          color="bg-green-100"
        />
      </div>
      <div 
        onClick={() => handleMetricCardClick('cancelled')}
        className="cursor-pointer hover:opacity-90 transition-opacity"
      >
        <MetricCard
          title="Cancelled/Declined"
          value={(bookingCounts.cancelled || 0) + (bookingCounts.declined || 0)}
          icon={<XCircle className="h-5 w-5 text-red-500" />}
          color="bg-red-100"
        />
      </div>
      {timeFilter !== "upcoming" && (
        <div 
          onClick={() => handleMetricCardClick('completed')}
          className="cursor-pointer hover:opacity-90 transition-opacity"
        >
          <MetricCard
            title="Completed"
            value={bookingCounts.completed}
            icon={<Star className="h-5 w-5 text-blue-500" />}
            color="bg-blue-100"
          />
        </div>
      )}
    </div>
  );
};
